import axios from "axios";

const API_ME_URL = "/user-app/v2/users/me";
const API_REFRESH_URL = "/user-app/v2/auth/token/refresh";
const API_LOGOUT_URL = "/user-app/v2/auth/logout";

const $session =
    {
        /* ===============================
         * 로그인 상태 보장
         * =============================== */
        async ensureLoggedIn()
        {
            const me = await this.fetchUserInfo();
            if (me) return true;

            // 로그인 이력이 없는 경우 refresh 시도하지 않음
            if (!this.isLogin()) return false;

            const refreshed = await this.callRefreshToken();
            if (!refreshed) return false;

            return !!(await this.fetchUserInfo());
        },

        /* ===============================
         * 사용자 정보 조회
         * =============================== */
        async fetchUserInfo()
        {
            try {
                const res = await axios.get(API_ME_URL, {
                    withCredentials: true,
                    _skipAuthRefresh: true
                });

                if (res.status === 200 && res.data) {
                    this.setUserInfoFromApi(res.data);
                    return res.data;
                }
                return null;
            } catch (e) {
                return null;
            }
        },

        setUserInfoFromApi(data)
        {
            const userNo = data.USER_NO ?? "GUEST";
            const userId = data.USER_ID ?? "";
            const email  = data.EMAIL ?? "";
            const name   = `${data.FIRST_NAME ?? ""}${data.LAST_NAME ?? ""}`.trim();

            localStorage.setItem("_USER_NO_", userNo);
            localStorage.setItem("_USER_ID_", userId);
            localStorage.setItem("_EMAIL_", email);
            if (name) localStorage.setItem("_NAME_", name);

            const resourceAccess = data.RESOURCE_ACCESS ?? {};
            localStorage.setItem(
                "_USER_RESOURCE_ACCESS_",
                typeof resourceAccess === "string"
                    ? resourceAccess
                    : JSON.stringify(resourceAccess)
            );
        },

        /* ===============================
         * 로그인 여부 (기존 isLogin 대체)
         * =============================== */
        isLogin()
        {
            const userNo = localStorage.getItem("_USER_NO_");
            return !!(userNo && userNo !== "GUEST");
        },

        /* ===============================
         * 사용자 정보 조회 (기존 JWT 파싱 대체)
         * =============================== */
        getUserInfo()
        {
            return {
                NAME: localStorage.getItem("_NAME_") || "",
                USER_ID: localStorage.getItem("_USER_ID_") || "",
                USER_NO: localStorage.getItem("_USER_NO_") || "GUEST"
            };
        },

        /* ===============================
         * 로그아웃
         * =============================== */
        async signOut()
        {
            try {
                await axios.delete(API_LOGOUT_URL, {
                    withCredentials: true,
                    _skipAuthRefresh: true
                });
            } catch (e) {
                // ignore logout error
            }

            localStorage.removeItem("_USER_RESOURCE_ACCESS_");
            localStorage.removeItem("_USER_NO_");
            localStorage.removeItem("_USER_ID_");
            localStorage.removeItem("_EMAIL_");
            localStorage.removeItem("_NAME_");
        },

        /* ===============================
         * 로그인 URL
         * =============================== */
        getSignInUrl()
        {
            const siteNo = process.env.VUE_APP_SITE_NO;
            const compNo = process.env.VUE_APP_COMP_NO;
            const siteUrl = process.env.VUE_APP_SITE_URL;
            const accountUrl = process.env.VUE_APP_ACCOUNT_URL;

            const callbackUrl = siteUrl + "/login";
            return `${accountUrl}/login?cb=${callbackUrl}&s=${siteNo}&c=${compNo}`;
        },

        setSignInUrl(siteNo, compNo)
        {
            let signInUrl = "/login?s=" + siteNo + "&c=" + compNo;
            sessionStorage.setItem("SIGNIN_URL", signInUrl);
        },

        /* ===============================
         * 토큰 갱신 (쿠키 기반)
         * =============================== */
        async callRefreshToken()
        {
            try {
                const res = await axios.post(API_REFRESH_URL, null, {
                    withCredentials: true,
                    _skipAuthRefresh: true
                });
                return res.status === 200;
            } catch (e) {
                return false;
            }
        },

        /* ===============================
         * 권한 조회
         * =============================== */
        getUserRoles(clientId)
        {
            const raw = localStorage.getItem("_USER_RESOURCE_ACCESS_");
            if (!raw) return [];

            let access;
            try {
                access = JSON.parse(raw);
            } catch (e) {
                return [];
            }

            if (!clientId) return access;
            return access[clientId]?.roles ?? [];
        }
    };

export { $session };